import React, { useState, useEffect } from 'react';
import { getMenus, getCategoriesByMenu, createCategory, deleteCategory } from '../services/api';
import { useTranslation } from '../contexts/LanguageContext';
import ConfirmModal from '../components/ConfirmModal';
import './Dishes.css';

const Categories = () => {
  const { t } = useTranslation();
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [message, setMessage] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  useEffect(() => {
    fetchMenus();
  }, []);

  useEffect(() => {
    if (selectedMenu) {
      fetchCategories(selectedMenu);
    }
  }, [selectedMenu]);

  const fetchMenus = async () => {
    try {
      const response = await getMenus();
      setMenus(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedMenu(response.data[0]._id);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch menus' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (menuId) => {
    try {
      const response = await getCategoriesByMenu(menuId);
      setCategories(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: t('failedToFetchCategories') });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim() || !selectedMenu) return;

    try {
      const response = await createCategory({ 
        name: newCategoryName, 
        menuId: selectedMenu 
      });
      setCategories([...categories, response.data]);
      setNewCategoryName('');
      setShowAddForm(false);
      setMessage({ type: 'success', text: t('categoryAdded') });
    } catch (error) {
      setMessage({ type: 'error', text: t('failedToAddCategory') });
    }
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      try {
        console.log('Attempting to delete category:', categoryToDelete);
        const response = await deleteCategory(categoryToDelete._id);
        console.log('Delete response:', response);
        setCategories(categories.filter(c => c._id !== categoryToDelete._id));
        setMessage({ type: 'success', text: t('categoryDeleted') });
      } catch (error) {
        console.error('Delete category error:', error);
        const errorMessage = error.response?.data?.error || error.message || t('failedToDeleteCategory');
        setMessage({ type: 'error', text: errorMessage });
      } finally {
        setIsConfirmModalOpen(false);
        setCategoryToDelete(null);
      }
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('categories')}</h1>
        <button 
          onClick={() => setShowAddForm(true)} 
          className="btn btn-primary add-dish-top-btn"
        >
          ➕ {t('addCategory')}
        </button>
      </div>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.type === 'success' ? '✓' : '✗'} {message.text}
        </div>
      )}

      <div className="card">
        <div style={{ marginBottom: '20px' }}>
          <label className="form-label">{t('selectMenu')}</label>
          <select 
            className="form-select"
            value={selectedMenu}
            onChange={(e) => setSelectedMenu(e.target.value)}
          >
            {menus.map((menu) => (
              <option key={menu._id} value={menu._id}>
                {menu.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 className="section-title">{t('categoriesFor')} {menus.find(m => m._id === selectedMenu)?.name || t('selectedMenu')}</h3>
        </div>

        {showAddForm && (
          <form onSubmit={handleAddCategory} style={{ marginBottom: '20px', padding: '20px', backgroundColor: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">{t('categoryName')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder={t('enterCategoryName')}
                  required
                />
              </div>
              <div>
                <button type="submit" className="btn btn-success">
                  ✓ {t('save')}
                </button>
              </div>
              <div>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowAddForm(false)}
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </form>
        )}

        {categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-category-icon">📭</div>
            <h3>{t('noCategoriesFound')}</h3>
            <p>{t('addFirstCategory')}</p>
          </div>
        ) : (
          <table className="dishes-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('menu')}</th>
                <th>{t('dishesCount')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {categories.filter(category => category && category._id).map((category) => (
                <tr key={category._id} className="dish-row">
                  <td className="dish-name-cell">
                    <span className="dish-name-table">🍽️ {category.name || 'Unnamed Category'}</span>
                  </td>
                  <td className="dish-category-cell">
                    <span className="dish-category-table">{menus.find(m => m._id === selectedMenu)?.name}</span>
                  </td>
                  <td className="dish-price-cell">
                    <span className="dish-price-table">-</span>
                  </td>
                  <td className="dish-status-cell">
                    <span className="status-badge active">
                      <span className="status-icon">✓</span>
                      {t('active')}
                    </span>
                  </td>
                  <td className="dish-actions-cell">
                    <div className="action-buttons">
                      <button 
                        className="btn-action btn-edit-table"
                        title={t('viewDishes')}
                      >
                        👁️
                      </button>
                      <button 
                        className="btn-action btn-delete-table"
                        onClick={() => handleDeleteCategory(category)}
                        title={t('delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isConfirmModalOpen && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onCancel={() => setIsConfirmModalOpen(false)}
          onConfirm={confirmDelete}
          message={`${t('confirmDelete')} "${categoryToDelete?.name}"?`}
        />
      )}
    </div>
  );
};

export default Categories;
